<template>
  <div class="survey-page">

    <!-- Carregando -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-success" style="width:3rem;height:3rem;" role="status"></div>
      <p class="mt-3 text-muted">Carregando formulário...</p>
    </div>

    <!-- Não encontrado / encerrado -->
    <div v-else-if="!form" class="d-flex justify-content-center align-items-center" style="min-height:80vh">
      <div class="card text-center" style="max-width:480px; width:100%">
        <div class="card-body py-5 px-4">
          <i class="fa fa-times-circle fa-3x text-danger mb-3 d-block"></i>
          <h4>Formulário não disponível</h4>
          <p class="text-muted">
            Este formulário não existe, não está ativo ou já foi encerrado.<br>
            Verifique o link e tente novamente.
          </p>
        </div>
      </div>
    </div>

    <!-- Sucesso -->
    <div v-else-if="enviado" class="d-flex justify-content-center align-items-center" style="min-height:80vh">
      <div class="card text-center" style="max-width:480px; width:100%">
        <div class="card-body py-5 px-4">
          <i class="fa fa-check-circle fa-3x text-success mb-3 d-block"></i>
          <h4>Resposta enviada com sucesso!</h4>
          <p class="text-muted">
            Obrigado por participar do diagnóstico participativo.<br>
            Suas respostas contribuirão com o Plano Municipal de Arborização Urbana.
          </p>
        </div>
      </div>
    </div>

    <!-- Formulário -->
    <div v-else class="survey-container mx-auto py-4 px-3">

      <!-- Cabeçalho -->
      <div class="card mb-4 border-0 shadow-sm overflow-hidden">
        <div class="survey-header text-white text-center py-4 px-3">
          <h3 class="mb-1 font-weight-bold">{{ form.title }}</h3>
          <p class="mb-0 font-weight-bold opacity-75">{{ form.conta_name }}</p>
        </div>
        <div v-if="form.description" class="card-body">
          <p class="text-muted mb-0" style="white-space:pre-line">{{ form.description }}</p>
        </div>
      </div>

      <!-- Seção: Identificação -->
      <div v-if="perguntasIdentificacao.length" class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <strong><i class="fa fa-user mr-2"></i>Identificação</strong>
          <small class="text-muted ml-2">— todas as perguntas são opcionais</small>
        </div>
        <div class="px-3 pt-3 pb-0">
          <p class="text-muted small mb-0">
            <i class="fa fa-lock mr-1"></i>
            Os dados de contato (telefone e e-mail) são opcionais e estarão protegidos pela LGPD.
          </p>
        </div>
        <div class="card-body">
          <div
            v-for="(q, i) in perguntasIdentificacao"
            :key="q.id"
            :class="{'border-bottom pb-4 mb-4': i < perguntasIdentificacao.length - 1}"
          >
            <label class="font-weight-bold d-block mb-2">
              {{ i + 1 }}. {{ q.question_text }}
              <span v-if="q.is_required" class="text-danger ml-1">*</span>
            </label>

            <!-- Telefone (máscara) -->
            <input
              v-if="q.code === 'id_telefone'"
              type="tel"
              class="form-control"
              :value="respostas[q.id] ? respostas[q.id].text_value : ''"
              @input="setTelefone(q, $event)"
              placeholder="(XX) XXXX-XXXX"
              maxlength="15"
            />

            <!-- Texto livre -->
            <input
              v-else-if="q.question_type === 'free_text'"
              type="text"
              class="form-control"
              :value="respostas[q.id] ? respostas[q.id].text_value : ''"
              @input="setFreeText(q, $event.target.value)"
              placeholder="Sua resposta..."
            />

            <!-- Escala numérica -->
            <div v-else-if="q.question_type === 'scale'">
              <div class="d-flex align-items-center flex-wrap">
                <small class="text-muted mr-3">{{ q.scale_min_label }}</small>
                <div class="d-flex">
                  <label
                    v-for="n in buildScale(q)"
                    :key="n"
                    class="mr-3 text-center"
                    style="cursor:pointer; min-width:36px"
                  >
                    <input
                      type="radio"
                      :name="'scale_' + q.id"
                      :value="n"
                      :checked="respostas[q.id] && respostas[q.id].scale_value === n"
                      @change="setScale(q, n)"
                      class="d-block mx-auto mb-1"
                    />
                    <span class="small font-weight-bold">{{ n }}</span>
                  </label>
                </div>
                <small class="text-muted ml-1">{{ q.scale_max_label }}</small>
              </div>
            </div>

            <!-- Escolha única -->
            <div v-else-if="q.question_type === 'single_choice'">
              <div v-for="opt in q.options" :key="opt.id" class="mb-2">
                <label class="d-flex align-items-center mb-0" style="cursor:pointer; font-weight:normal">
                  <input
                    type="radio"
                    :name="'radio_' + q.id"
                    :value="opt.id"
                    :checked="isSelected(q, opt.id)"
                    @change="selectSingle(q, opt)"
                    class="mr-2"
                  />
                  {{ opt.text }}
                </label>
              </div>
            </div>

            <!-- Múltipla escolha -->
            <div v-else-if="q.question_type === 'multiple_choice'">
              <div v-for="opt in q.options" :key="opt.id" class="mb-2">
                <label class="d-flex align-items-center mb-0" style="cursor:pointer; font-weight:normal">
                  <input
                    type="checkbox"
                    :checked="isSelected(q, opt.id)"
                    @change="toggleMulti(q, opt, $event)"
                    class="mr-2"
                  />
                  {{ opt.text }}
                </label>
              </div>
              <div v-if="q.allows_other" class="mt-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :value="respostas[q.id] ? respostas[q.id].other_text : ''"
                  placeholder="Outro — especifique..."
                  @input="patchOther(q, $event.target.value)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Seção: Avaliação -->
      <div v-if="perguntasAvaliacao.length" class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <strong><i class="fa fa-leaf mr-2"></i>Avaliação da Arborização Urbana</strong>
        </div>
        <div class="card-body">
          <div
            v-for="(q, i) in perguntasAvaliacao"
            :key="q.id"
            :class="{'border-bottom pb-4 mb-4': i < perguntasAvaliacao.length - 1}"
          >
            <label class="font-weight-bold d-block mb-2">
              {{ i + 1 }}. {{ q.question_text }}
              <span v-if="q.is_required" class="text-danger ml-1">*</span>
            </label>

            <!-- Texto livre -->
            <textarea
              v-if="q.question_type === 'free_text'"
              class="form-control"
              rows="3"
              :value="respostas[q.id] ? respostas[q.id].text_value : ''"
              @input="setFreeText(q, $event.target.value)"
              placeholder="Sua resposta..."
            ></textarea>

            <!-- Escala numérica -->
            <div v-else-if="q.question_type === 'scale'">
              <div class="d-flex align-items-center flex-wrap">
                <small class="text-muted mr-3">{{ q.scale_min_label }}</small>
                <div class="d-flex">
                  <label
                    v-for="n in buildScale(q)"
                    :key="n"
                    class="mr-3 text-center"
                    style="cursor:pointer; min-width:36px"
                  >
                    <input
                      type="radio"
                      :name="'scale_' + q.id"
                      :value="n"
                      :checked="respostas[q.id] && respostas[q.id].scale_value === n"
                      @change="setScale(q, n)"
                      class="d-block mx-auto mb-1"
                    />
                    <span class="small font-weight-bold">{{ n }}</span>
                  </label>
                </div>
                <small class="text-muted ml-1">{{ q.scale_max_label }}</small>
              </div>
            </div>

            <!-- Escolha única -->
            <div v-else-if="q.question_type === 'single_choice'">
              <div v-for="opt in q.options" :key="opt.id" class="mb-2">
                <label class="d-flex align-items-center mb-0" style="cursor:pointer; font-weight:normal">
                  <input
                    type="radio"
                    :name="'radio_' + q.id"
                    :value="opt.id"
                    :checked="isSelected(q, opt.id)"
                    @change="selectSingle(q, opt)"
                    class="mr-2"
                  />
                  {{ opt.text }}
                </label>
              </div>
            </div>

            <!-- Múltipla escolha -->
            <div v-else-if="q.question_type === 'multiple_choice'">
              <div v-for="opt in q.options" :key="opt.id" class="mb-2">
                <label class="d-flex align-items-center mb-0" style="cursor:pointer; font-weight:normal">
                  <input
                    type="checkbox"
                    :checked="isSelected(q, opt.id)"
                    @change="toggleMulti(q, opt, $event)"
                    class="mr-2"
                  />
                  {{ opt.text }}
                </label>
              </div>
              <div v-if="q.allows_other" class="mt-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :value="respostas[q.id] ? respostas[q.id].other_text : ''"
                  placeholder="Outro — especifique..."
                  @input="patchOther(q, $event.target.value)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Botão enviar -->
      <div class="card mb-5 shadow-sm">
        <div class="card-body text-center py-4">
          <p v-if="!formularioValido" class="text-danger small mb-3">
            <i class="fa fa-exclamation-circle mr-1"></i>
            Preencha todas as perguntas obrigatórias (marcadas com *) antes de enviar.
          </p>
          <button
            class="btn btn-success btn-lg px-5"
            :disabled="enviando || !formularioValido"
            @click="enviar"
          >
            <span v-if="enviando">
              <span class="spinner-border spinner-border-sm mr-2"></span>Enviando...
            </span>
            <span v-else>
              <i class="fa fa-paper-plane mr-2"></i>Enviar minha resposta
            </span>
          </button>
          <p class="text-muted small mt-3 mb-0">
            As respostas são anônimas e serão usadas exclusivamente para fins de planejamento urbano.
          </p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import SurveyService from "../../servico/SurveyService";

export default {
  name: "SurveyFormPublic",
  data() {
    return {
      loading: true,
      form: null,
      respostas: {},
      enviando: false,
      enviado: false,
      originalTitle: ""
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    perguntasIdentificacao() {
      if (!this.form) return [];
      return this.form.questions.filter((q) => q.category === "identification");
    },
    perguntasAvaliacao() {
      if (!this.form) return [];
      return this.form.questions.filter((q) => q.category === "evaluation");
    },
    formularioValido() {
      if (!this.form) return false;
      return this.form.questions
        .filter((q) => q.is_required)
        .every((q) => {
          const r = this.respostas[q.id];
          if (!r) return false;
          if (q.question_type === "free_text") return !!(r.text_value && r.text_value.trim());
          if (q.question_type === "scale") return r.scale_value != null;
          return r.selected_options && r.selected_options.length > 0;
        });
    }
  },
  mounted() {
    this.originalTitle = document.title;
    SurveyService.GetPublicForm(this.token)
      .then((r) => {
        this.form = r.data;
        document.title = `${r.data.conta_name} - Diagnóstico Participativo da Arborização Urbana`;
        const init = {};
        r.data.questions.forEach((q) => { init[q.id] = null; });
        this.respostas = init;
      })
      .catch(() => {
        this.form = null;
      })
      .finally(() => {
        this.loading = false;
      });
  },
  beforeDestroy() {
    document.title = this.originalTitle;
  },
  methods: {
    buildScale(q) {
      const arr = [];
      for (let i = q.scale_min || 1; i <= (q.scale_max || 5); i++) arr.push(i);
      return arr;
    },
    baseResposta(q) {
      return {
        question_id: q.id,
        source: q.source,
        question_type: q.question_type
      };
    },
    maskPhone(value) {
      const digits = value.replace(/\D/g, "").slice(0, 11);
      if (digits.length <= 2) return digits.length ? `(${digits}` : "";
      if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    },
    setTelefone(q, evt) {
      const masked = this.maskPhone(evt.target.value);
      evt.target.value = masked;
      this.$set(this.respostas, q.id, { ...this.baseResposta(q), text_value: masked });
    },
    setFreeText(q, value) {
      this.$set(this.respostas, q.id, { ...this.baseResposta(q), text_value: value });
    },
    setScale(q, n) {
      this.$set(this.respostas, q.id, { ...this.baseResposta(q), scale_value: n });
    },
    isSelected(q, optId) {
      const r = this.respostas[q.id];
      return !!(r && (r.selected_options || []).some((o) => o.id === optId));
    },
    selectSingle(q, opt) {
      this.$set(this.respostas, q.id, {
        ...this.baseResposta(q),
        selected_options: [{ id: opt.id, text: opt.text, weight: opt.weight }]
      });
    },
    toggleMulti(q, opt, evt) {
      const current = this.respostas[q.id] && this.respostas[q.id].selected_options
        ? [...this.respostas[q.id].selected_options]
        : [];
      if (evt.target.checked) {
        current.push({ id: opt.id, text: opt.text, weight: opt.weight });
      } else {
        const idx = current.findIndex((o) => o.id === opt.id);
        if (idx !== -1) current.splice(idx, 1);
      }
      this.$set(this.respostas, q.id, {
        ...(this.respostas[q.id] || this.baseResposta(q)),
        selected_options: current
      });
    },
    patchOther(q, text) {
      this.$set(this.respostas, q.id, {
        ...(this.respostas[q.id] || this.baseResposta(q)),
        other_text: text
      });
    },
    enviar() {
      if (!this.formularioValido || this.enviando) return;
      this.enviando = true;
      const lista = Object.values(this.respostas).filter(Boolean);
      SurveyService.SubmitResponse(this.token, lista)
        .then(() => {
          this.enviado = true;
          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .catch(() => {
          alert("Erro ao enviar resposta. Por favor, tente novamente.");
        })
        .finally(() => {
          this.enviando = false;
        });
    }
  }
};
</script>

<style scoped>
.survey-page {
  min-height: 100vh;
  background-color: #f4f6f0;
  padding: 1rem;
}
.survey-container {
  max-width: 780px;
}
.survey-header {
  background: linear-gradient(135deg, #1b4332, #52b788);
}
.opacity-75 {
  opacity: 0.75;
}
</style>
